import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';

import { UnrealBloomPass } from 'three/examples/jsm/Addons.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { GammaCorrectionShader } from 'three/examples/jsm/shaders/GammaCorrectionShader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { ACESFilmicToneMappingShader } from 'three/examples/jsm/Addons.js';
import * as dat from 'lil-gui';


// Base
const gui = new dat.GUI();
gui.hide(); // Show GUI by default for tweaking
const canvas = document.querySelector('canvas.webgl');
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x232323);

// Enable color management globally
THREE.ColorManagement.enabled = true;

const options = {
  enableSwoopingCamera: false,
  enableRotation: false,
  color: 0x160808,
  metalness: 0.2,
  roughness: 0.6,
  transmission: 0.8,
  ior: 1.6,
  reflectivity: 0.18,
  thickness: 2.0, // Slight thickness to catch faint light
  envMapIntensity: 1.4, // Minimal environment reflection
  clearcoat: 0.,
  clearcoatRoughness: 0, // Very rough for matte
  normalScale: 0.0,
  clearcoatNormalScale: 0.0,
  normalRepeat: 1,
  attenuationTint: 0x0d1a0d, // Pure black tint
  attenuationDistance: 1.4, // Strong light absorption
  bloomThreshold: 0.9,
  bloomStrength: 0.2,
  bloomRadius: 0.2,
  opacity: 1, // Mostly opaque
};



const hdrEquirect = new RGBELoader().load(
  "/sky.hdr",
  () => {
    hdrEquirect.mapping = THREE.EquirectangularReflectionMapping;
    hdrEquirect.colorSpace = THREE.SRGBColorSpace; 
  }
);

const textureLoader = new THREE.TextureLoader();



const normalMapTexture = textureLoader.load("/normal.jpg");
normalMapTexture.wrapS = THREE.RepeatWrapping;
normalMapTexture.wrapT = THREE.RepeatWrapping;
normalMapTexture.repeat.set(options.normalRepeat, options.normalRepeat);
normalMapTexture.colorSpace = THREE.LinearSRGBColorSpace; // Normal maps should be linear

function isMobileDevice() {
  return (typeof window.orientation !== "undefined") || (navigator.userAgent.indexOf('IEMobile') !== -1);
}

const material = new THREE.MeshPhysicalMaterial({
  color: options.color,
  metalness: options.metalness,
  roughness: options.roughness,
  transmission: options.transmission,
  ior: options.ior,
  reflectivity: options.reflectivity,
  thickness: options.thickness,
  envMap: hdrEquirect,
  envMapIntensity: options.envMapIntensity,
  clearcoat: options.clearcoat,
  clearcoatRoughness: options.clearcoatRoughness,
  normalScale: new THREE.Vector2(options.normalScale),
  normalMap: normalMapTexture,
  opacity: options.opacity,
  transparent: true,
  clearcoatNormalMap: normalMapTexture,
  clearcoatNormalScale: new THREE.Vector2(options.clearcoatNormalScale),
  attenuationTint: new THREE.Color(options.attenuationTint),
  attenuationDistance: options.attenuationDistance,
});

// material.depthWrite = false;
material.transparent = true;

function findGeometry(model) {
  let foundGeometry = null;

  model.traverse((node) => {
    if (node.isMesh && node.geometry) {
      foundGeometry = node.geometry.clone();
      return; // Stop traversing once we find the first geometry
    }
  });

  return foundGeometry;
}



const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("/draco/");
dracoLoader.setDecoderConfig({ type: 'js' });

const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);

gltfLoader.load("/glass1.glb", (gltf) => {
  const model = gltf.scene;

  // Find any geometry in the model
  const glassGeometry = findGeometry(model);
  glassGeometry.rotateX(Math.PI / -2);
  glassGeometry.translate(0, -1, 0);



  if (glassGeometry) {
    const glassMesh = new THREE.Mesh(glassGeometry, material);
    if (isMobileDevice()) {
      glassMesh.scale.set(0.015, 0.015, 0.015);
      glassMesh.position.set(0, -.7, 0);
    } else {
      glassMesh.scale.set(0.023, 0.023, 0.023);
      glassMesh.position.set(0, -1, 0);
    }


    // Adjust position as needed
    scene.add(glassMesh);
  } else {
    console.error("Failed to find geometry in GLB model.");
  }

  // Dispose of the original model to free up resources
  model.traverse((node) => {
    if (node.isMesh) {
      node.geometry.dispose();
      node.material.dispose();
    }
  });

}, undefined, (error) => {
  console.error(error);
});


//CAMERA
const camera = new THREE.PerspectiveCamera(35, window.innerWidth / window.innerHeight, 0.1, 15);
camera.position.set(5, 0, -0.05);
scene.add(camera);


// Sizes
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight
};
window.addEventListener('resize', () => {
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  effectComposer.setSize(sizes.width, sizes.height);
  effectComposer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});




const controls = new TrackballControls(camera, canvas);

controls.enableDamping = true;
controls.noZoom = true;
controls.noPan = true;

controls.mouseButtons = {
  LEFT: THREE.MOUSE.ROTATE,
  MIDDLE: THREE.MOUSE.ROTATE,
  RIGHT: THREE.MOUSE.ROTATE,
};
controls.rotateSpeed = 0.8;

//////////////////////////////////////////////////
//// ON MOUSE MOVE TO GET CAMERA POSITION
// document.addEventListener('mousemove', (event) => {
//     event.preventDefault()

//     console.log(camera.position)

// }, false)


// Renderer
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  antialias: true,
});
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFShadowMap;
renderer.useLegacyLights = false; // Use modern lighting
renderer.outputColorSpace = THREE.SRGBColorSpace; // Updated color space
renderer.toneMapping = THREE.ACESFilmicToneMapping; // Better tone mapping
renderer.toneMappingExposure = 1.0; // More neutral exposure
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

// Post processing
const renderTarget = new THREE.WebGLRenderTarget(800, 600, { samples: 2 });
const effectComposer = new EffectComposer(renderer, renderTarget);
effectComposer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
effectComposer.setSize(sizes.width, sizes.height);

// Render pass
const renderPass = new RenderPass(scene, camera);
effectComposer.addPass(renderPass);



// ACESFILMIC  pass - disable since we're using ACES on renderer
const ACESFilmShiftPass = new ShaderPass(ACESFilmicToneMappingShader);
ACESFilmShiftPass.enabled = false; // Disable to avoid double tone mapping
effectComposer.addPass(ACESFilmShiftPass);

//bloom pass 
const bloomPass = new UnrealBloomPass(

  new THREE.Vector2(sizes.width, sizes.height),
  options.bloomStrength,
  options.bloomRadius,
  options.bloomThreshold
);
effectComposer.addPass(bloomPass);


// Gamma correction pass
const gammaCorrectionPass = new ShaderPass(GammaCorrectionShader);
effectComposer.addPass(gammaCorrectionPass);

// Antialias pass
if (renderer.getPixelRatio() === 1 && !renderer.capabilities.isWebGL2) {
  const smaaPass = new SMAAPass();
  effectComposer.addPass(smaaPass);
}

// Material Controls - organized in folders
const materialFolder = gui.addFolder("Material Properties");
materialFolder.addColor(options, "color").onChange((val) => {
  material.color.set(val);
});
materialFolder.add(options, "opacity", 0, 1, 0.01).onChange((val) => {
  material.opacity = val;
});
materialFolder.add(options, "metalness", 0, 1, 0.01).onChange((val) => {
  material.metalness = val;
});
materialFolder.add(options, "roughness", 0, 1, 0.01).onChange((val) => {
  material.roughness = val;
});

const glassFolder = gui.addFolder("Glass Properties");
glassFolder.add(options, "transmission", 0, 1, 0.01).onChange((val) => {
  material.transmission = val;
});
glassFolder.add(options, "ior", 1, 2.33, 0.01).onChange((val) => {
  material.ior = val;
});
glassFolder.add(options, "thickness", 0, 5, 0.1).onChange((val) => {
  material.thickness = val;
});
glassFolder.addColor(options, "attenuationTint").onChange((val) => {
  material.attenuationTint.set(val);
});
glassFolder.add(options, "attenuationDistance", 0, 5, 0.01).onChange((val) => {
  material.attenuationDistance = val;
});

const reflectionFolder = gui.addFolder("Reflection & Environment");
reflectionFolder.add(options, "reflectivity", 0, 4, 0.01).onChange((val) => {
  material.reflectivity = val;
});
reflectionFolder.add(options, "envMapIntensity", 0, 20, 0.1).onChange((val) => {
  material.envMapIntensity = val;
});
reflectionFolder.add({ envRotation: 50 }, "envRotation", 0, Math.PI * 2, 0.01).name("Env Rotation").onChange((val) => {
  hdrEquirect.rotation = val;
  material.needsUpdate = true;
});

const clearcoatFolder = gui.addFolder("Clearcoat");
clearcoatFolder.add(options, "clearcoat", 0, 1, 0.01).onChange((val) => {
  material.clearcoat = val;
});
clearcoatFolder.add(options, "clearcoatRoughness", 0, 1, 0.01).onChange((val) => {
  material.clearcoatRoughness = val;
});
clearcoatFolder.add(options, "clearcoatNormalScale", 0, 5, 0.01).onChange((val) => {
  material.clearcoatNormalScale.set(val, val);
});

const normalFolder = gui.addFolder("Normal Maps");
normalFolder.add(options, "normalScale", 0, 5, 0.01).onChange((val) => {
  material.normalScale.set(val, val);
});
normalFolder.add(options, "normalRepeat", 1, 4, 1).onChange((val) => {
  normalMapTexture.repeat.set(val, val);
});

// Preset buttons for quick material changes
const presetsFolder = gui.addFolder("Material Presets");

const presets = {
  matteBlackGlass: () => {
    options.color = 0x1a1a1a;
    options.metalness = 0.1;
    options.roughness = 0.8;
    options.transmission = 0.7;
    options.reflectivity = 0.2;
    options.envMapIntensity = 0.3;
    options.clearcoat = 0.3;
    options.clearcoatRoughness = 0.9;
    options.opacity = 0.85;
    options.attenuationTint = 0x000000;
    options.attenuationDistance = 1.0;
    updateMaterial();
  },
  clearGlass: () => {
    options.color = 0xffffff;
    options.metalness = 0;
    options.roughness = 0.1;
    options.transmission = 1;
    options.reflectivity = 0.5;
    options.envMapIntensity = 1.5;
    options.clearcoat = 1;
    options.clearcoatRoughness = 0.1;
    options.opacity = 0.48;
    options.attenuationTint = 0xffffff;
    options.attenuationDistance = 0;
    updateMaterial();
  },
  frostedGlass: () => {
    options.color = 0xf0f0f0;
    options.metalness = 0;
    options.roughness = 0.6;
    options.transmission = 0.8;
    options.reflectivity = 0.3;
    options.envMapIntensity = 0.8;
    options.clearcoat = 0.5;
    options.clearcoatRoughness = 0.7;
    options.opacity = 0.7;
    options.attenuationTint = 0xffffff;
    options.attenuationDistance = 0.5;
    updateMaterial();
  },
  tintedGlass: () => {
    options.color = 0x1a2d1a; // Darker green tint
    options.metalness = 0;
    options.roughness = 0.3;
    options.transmission = 0.8; // Slightly reduced transmission for more opacity
    options.reflectivity = 0.4;
    options.envMapIntensity = 1.2;
    options.clearcoat = 0.8;
    options.clearcoatRoughness = 0.2;
    options.opacity = 0.7; // Increased opacity for darker appearance
    options.attenuationTint = 0x0d1a0d; // Much darker green for light absorption
    options.attenuationDistance = 1.5; // Reduced distance for stronger absorption
    updateMaterial();
  }
};

presetsFolder.add(presets, "matteBlackGlass").name("Matte Black Glass");
presetsFolder.add(presets, "clearGlass").name("Clear Glass");
presetsFolder.add(presets, "frostedGlass").name("Frosted Glass");
presetsFolder.add(presets, "tintedGlass").name("Tinted Glass");

// Function to update material with current options
function updateMaterial() {
  material.color.set(options.color);
  material.metalness = options.metalness;
  material.roughness = options.roughness;
  material.transmission = options.transmission;
  material.reflectivity = options.reflectivity;
  material.envMapIntensity = options.envMapIntensity;
  material.clearcoat = options.clearcoat;
  material.clearcoatRoughness = options.clearcoatRoughness;
  material.opacity = options.opacity;
  material.attenuationTint.set(options.attenuationTint);
  material.attenuationDistance = options.attenuationDistance;

  // Update GUI to reflect changes
  gui.controllersRecursive().forEach(controller => {
    controller.updateDisplay();
  });
}

const postprocessing = gui.addFolder("Post Processing");

postprocessing.add(options, "bloomThreshold", 0, 1, 0.01).onChange((val) => {
  bloomPass.threshold = val;
});

postprocessing.add(options, "bloomStrength", 0, 5, 0.01).onChange((val) => {
  bloomPass.strength = val;
});

postprocessing.add(options, "bloomRadius", 0, 1, 0.01).onChange((val) => {
  bloomPass.radius = val;
});

// Add tone mapping controls for color accuracy
const colorFolder = gui.addFolder("Color Management");
const toneMappingOptions = {
  'None': THREE.NoToneMapping,
  'Linear': THREE.LinearToneMapping,
  'Reinhard': THREE.ReinhardToneMapping,
  'Cineon': THREE.CineonToneMapping,
  'ACES Filmic': THREE.ACESFilmicToneMapping
};

colorFolder.add(renderer, 'toneMapping', toneMappingOptions).onChange(() => {
  renderer.toneMapping = renderer.toneMapping;
});

colorFolder.add(renderer, 'toneMappingExposure', 0, 3, 0.01).name('Exposure');

colorFolder.add(ACESFilmShiftPass, 'enabled').name('ACES Post Pass').onChange((val) => {
  // Toggle between renderer ACES and post-processing ACES
  if (val) {
    renderer.toneMapping = THREE.LinearToneMapping;
  } else {
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
  }
});

// Keyboard controls
document.addEventListener('keydown', (event) => {
  if (event.key === 'h' || event.key === 'H') {
    if (gui._hidden) {
      gui.show();
    } else {
      gui.hide();
    }
  }
});

// Animate
const clock = new THREE.Clock();
const tick = () => {
  const elapsedTime = clock.getElapsedTime();
  controls.update();
  effectComposer.render();
  window.requestAnimationFrame(tick);
};

tick();

// Function to update all materials
const updateAllMaterials = () => {
  scene.traverse((child) => {
    if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
      child.material.envMapIntensity = 1.5;
      child.material.needsUpdate = true;
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
};